import { useTranslation } from 'react-i18next';
import { useSync } from '../context/SyncContext';
import { OFFLINE, RUNNING, explainConflict, losesWork } from '../utils/syncEngine';

/*
 * What the person is told about syncing, and when.
 *
 * The rule this follows: say nothing while everything is fine. A banner that
 * is always there is furniture, and the one time it says something urgent
 * nobody reads it. So an idle, empty, conflict-free queue renders NOTHING.
 *
 * A conflict that lost work is different. `reject_older` means somebody's
 * edit was discarded -- the one place this design loses what a person typed
 * -- and it says so in words, names when each version was written, and stays
 * until it is dismissed. `reject_same` lost nothing and is reported quietly.
 */

export default function SyncStatus() {
  const { t } = useTranslation('sync');
  const { state, pending, conflicts, lastError, sync, dismissConflict } = useSync();

  const losses = conflicts.filter(losesWork);
  const quiet = state !== OFFLINE && state !== RUNNING && !pending && !losses.length && !lastError;
  if (quiet) return null;

  return (
    <div data-testid="sync-status" role="status" aria-live="polite"
         style={{ padding: '8px 16px', background: 'var(--c-bg-muted)',
                  borderBottom: '1px solid var(--c-border)', fontSize: 14 }}>
      {state === OFFLINE && (
        <span data-testid="sync-offline">
          {t('offline', { count: pending })}
        </span>
      )}
      {state === RUNNING && <span data-testid="sync-running">{t('running')}</span>}
      {state !== OFFLINE && state !== RUNNING && pending > 0 && (
        <span data-testid="sync-pending">{t('pending', { count: pending })}</span>
      )}

      {lastError && (
        <span data-testid="sync-error" style={{ marginInlineStart: 8 }}>
          {t('failed', { error: lastError })}
        </span>
      )}

      {pending > 0 && state !== RUNNING && state !== OFFLINE && (
        <button type="button" data-testid="sync-now" onClick={() => sync({ force: true })} title={t('tip.syncNow')}
                style={{ marginInlineStart: 12 }}>
          {t('syncNow')}
        </button>
      )}

      {losses.length > 0 && (
        <div data-testid="sync-conflicts" role="alert" style={{ marginTop: 8 }}>
          <strong>{t('conflicts.heading', { count: losses.length })}</strong>
          <ul>
            {losses.map((conflict) => (
              <li key={conflict.id} data-testid={`conflict-${conflict.id}`}>
                <code>{conflict.table}</code>{' '}
                {explainConflict(conflict)}
                {conflict.incoming_updated_at && conflict.existing_updated_at && (
                  <span style={{ color: 'var(--c-text-muted)' }}>
                    {' '}({t('conflicts.yours', { at: conflict.incoming_updated_at })};{' '}
                    {t('conflicts.theirs', { at: conflict.existing_updated_at })})
                  </span>
                )}
                <button type="button" onClick={() => dismissConflict(conflict.id)}
                        data-testid={`dismiss-${conflict.id}`} title={t('tip.dismiss')} style={{ marginInlineStart: 8 }}>
                  {t('conflicts.dismiss')}
                </button>
              </li>
            ))}
          </ul>
          <p style={{ color: 'var(--c-text-muted)' }}>{t('conflicts.note')}</p>
        </div>
      )}
    </div>
  );
}
